import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactDatatable from "@mkikets/react-datatable";
import { useGetUserEtokenBalancesQuery, useGetAllUsersListQuery, useLazyGetStakingTransactionsQuery } from "../../../redux/userApi";
import { useGetCryptocurrencyQuery } from "../../../redux/cryptocurrencyApi";
import moment from "moment";
import { Button, Form, Row, Col, Modal } from "react-bootstrap";
import Select from 'react-select';

function UserEtokenBalances() {
    const { t } = useTranslation();

    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        user_id: "",
        cryptocurrency_id: ""
    });

    const [filters, setFilters] = useState({
        user_id: "",
        cryptocurrency_id: ""
    });

    const { data: cryptoResponse } = useGetCryptocurrencyQuery();
    const cryptocurrencies = cryptoResponse?.data || [];

    const { data: userResponse } = useGetAllUsersListQuery();
    const users = userResponse?.data || [];

    const userOptions = [
        { value: "", label: t('select_user') || "All Users" },
        ...users.map(user => ({
            value: user.id,
            label: String(user.mobile || user.email || user.fname || user.id)
        }))
    ];

    const cryptoOptions = [
        { value: "", label: t('select_cryptocurrency') || "All Cryptocurrencies" },
        ...cryptocurrencies.map(crypto => ({
            value: crypto.id,
            label: `${crypto.coin_symbol} ${crypto.symbol ? `(${crypto.symbol})` : ''}`
        }))
    ];

    const { data: apiResponse, isLoading, isFetching } = useGetUserEtokenBalancesQuery(params);
    const balances = apiResponse?.data || [];
    const totalRecords = apiResponse?.total_records || 0;

    const [showTransactionsModal, setShowTransactionsModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalParams, setModalParams] = useState({ page: 1, limit: 10 });
    const [fetchTransactions, { data: txResponse }] = useLazyGetStakingTransactionsQuery();
    const transactions = txResponse?.data || [];
    const txTotalRecords = txResponse?.totalCount || 0;

    const handleUserSelect = (selectedOption) => {
        setFilters(prev => ({ ...prev, user_id: selectedOption ? selectedOption.value : "" }));
    };

    const handleCryptoSelect = (selectedOption) => {
        setFilters(prev => ({ ...prev, cryptocurrency_id: selectedOption ? selectedOption.value : "" }));
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            color: '#000',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#000',
        }),
        input: (provided) => ({
            ...provided,
            color: '#000',
        }),
        option: (provided, state) => ({
            ...provided,
            color: '#000',
            backgroundColor: state.isFocused ? '#e9ecef' : '#fff',
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#fff',
        })
    };

    const handleSearchSubmit = () => {
        setParams({
            ...params,
            page: 1,
            user_id: filters.user_id,
            cryptocurrency_id: filters.cryptocurrency_id
        });
    };

    const handleClearFilters = () => {
        setFilters({ user_id: "", cryptocurrency_id: "" });
        setParams({
            ...params,
            page: 1,
            user_id: "",
            cryptocurrency_id: ""
        });
    };

    const handleTableChange = (newParams) => {
        setParams({
            ...params,
            page: newParams.page_number,
            limit: newParams.page_size,
        });
    };

    const handleShowTransactions = (record) => {
        setSelectedRecord(record);
        setModalParams({ page: 1, limit: 10 });
        setShowTransactionsModal(true);
        fetchTransactions({
            page: 1,
            limit: 10,
            user_id: record.user_id,
            cryptocurrency_id: record.cryptocurrency_id
        });
    };

    const handleModalTableChange = (newParams) => {
        setModalParams({
            page: newParams.page_number,
            limit: newParams.page_size,
        });
        if (selectedRecord) {
            fetchTransactions({
                page: newParams.page_number,
                limit: newParams.page_size,
                user_id: selectedRecord.user_id,
                cryptocurrency_id: selectedRecord.cryptocurrency_id
            });
        }
    };

    const columns = [
        {
            key: "srno.",
            text: t('sr_no') || "Sr No.",
            className: "sr_no.",
            align: "left",
            sortable: false,
            cell: (row, index) => (params.page - 1) * params.limit + index + 1,
        },
        {
            key: "mobile",
            text: t('mobile') || "Mobile",
            className: "mobile",
            align: "left",
            sortable: false,
            cell: (record) => record?.mobile || "-"
        },
        {
            key: "cryptocurrency_symbol",
            text: t('cryptocurrency') || "Cryptocurrency",
            className: "cryptocurrency",
            align: "left",
            sortable: false,
            cell: (record) => {
                if (!record?.coin_symbol) return "-";
                return `${record.coin_symbol} ${record.network_symbol ? `(${record.network_symbol})` : ""}`.trim();
            }
        },
        {
            key: "balance",
            text: t('balance') || "Balance",
            className: "balance",
            align: "left",
            sortable: false,
            cell: (record) => record.balance || "0"
        },
        {
            key: "created_at",
            text: t('created_at') || "Created At",
            className: "created_at",
            align: "left",
            sortable: false,
            cell: (record) => {
                const date = record.created_at;
                return <>{date ? moment(date).format("DD/MM/YYYY HH:mm:ss") : "-"}</>;
            },
        },
        {
            key: "action",
            text: t('action') || "Action",
            className: "action",
            align: "left",
            sortable: false,
            cell: (record) => (
                <Button variant="info" size="sm" onClick={() => handleShowTransactions(record)}>
                    {t('transactions') || "Transactions"}
                </Button>
            )
        }
    ];

    const config = {
        page_size: 10,
        length_menu: [10, 20, 50],
        filename: "User Etoken Balances List",
        no_data_text: t('no_record_found') || "No records found",
        language: {
            filter: t('search_in_records') || "Search in records...",
            pagination: {
                first: t('first') || "First",
                previous: t('previous') || "Previous",
                next: t('next') || "Next",
                last: t('last') || "Last",
            },
        },
        show_length_menu: true,
        show_filter: false,
        show_pagination: true,
        show_info: true,
    };

    return (
        <section className="section dashboard">
            <div className="pagetitle d-flex align-items-center justify-content-between mb-4">
                <h1>{t('staking_etoken_balance') || "Staking Etoken Balance"}</h1>
            </div>

            <div className="card mb-4">
                <div className="card-body pt-4">
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Group controlId="filterUserId">
                                <Form.Label>{t('user_id') || "User ID"}</Form.Label>
                                <Select
                                    options={userOptions}
                                    value={userOptions.find(opt => opt.value === filters.user_id) || userOptions[0]}
                                    onChange={handleUserSelect}
                                    isSearchable
                                    styles={customStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="filterCryptoId">
                                <Form.Label>{t('cryptocurrency') || "Cryptocurrency"}</Form.Label>
                                <Select
                                    options={cryptoOptions}
                                    value={cryptoOptions.find(opt => opt.value === filters.cryptocurrency_id) || cryptoOptions[0]}
                                    onChange={handleCryptoSelect}
                                    isSearchable
                                    styles={customStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex align-items-end gap-2">
                            <Button variant="primary" onClick={handleSearchSubmit}>
                                {t('search') || "Search"}
                            </Button>
                            <Button variant="secondary" onClick={handleClearFilters}>
                                {t('clear') || "Clear"}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card">
                <div className="card-body pt-4 position-relative">
                    {(isLoading || isFetching) && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    <ReactDatatable
                        key={`datatable-user-etoken-balances-${params.page}-${params.limit}`}
                        config={config}
                        records={balances}
                        columns={columns}
                        dynamic={true}
                        total_record={totalRecords}
                        onChange={handleTableChange}
                    />
                </div>
            </div>

            <Modal show={showTransactionsModal} onHide={() => setShowTransactionsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('transactions') || "Transactions"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ReactDatatable
                        key={`datatable-tx-${modalParams.page}-${modalParams.limit}`}
                        config={{
                            ...config,
                            filename: "Transactions List",
                            show_filter: false,
                        }}
                        records={transactions}
                        columns={[
                            {
                                key: "srno.",
                                text: t('sr_no') || "Sr No.",
                                className: "sr_no.",
                                align: "left",
                                sortable: false,
                                cell: (row, index) => (modalParams.page - 1) * modalParams.limit + index + 1,
                            },
                            {
                                key: "amount",
                                text: t('amount') || "Amount",
                                className: "amount",
                                align: "left",
                                sortable: false,
                                cell: (record) => record.amount || "0"
                            },
                            {
                                key: "tx_type",
                                text: t('type') || "Type",
                                className: "tx_type",
                                align: "left",
                                sortable: false,
                                cell: (record) => record.tx_type || "-"
                            },
                            {
                                key: "created_at",
                                text: t('date') || "Date",
                                className: "created_at",
                                align: "left",
                                sortable: false,
                                cell: (record) => record.created_at ? moment(record.created_at).format("DD/MM/YYYY HH:mm:ss") : "-"
                            }
                        ]}
                        dynamic={true}
                        total_record={txTotalRecords}
                        onChange={handleModalTableChange}
                    />
                </Modal.Body>
            </Modal>
        </section>
    );
}

export default UserEtokenBalances;
